"use client";

import Ad from 'src/components/ads/Ad';
import Filter from 'src/components/streaming/weeklystreamingreleases/Filter';
import type IWeeklyReleases from 'src/components/streaming/weeklystreamingreleases/IWeeklyReleases';
import StreamingReleasesForWeek from 'src/components/streaming/weeklystreamingreleases/StreamingReleasesForWeek';
import type { SupportsScrollRestoration } from "src/lib/client/withScrollRestoration";
import withScrollRestoration from "src/lib/client/withScrollRestoration";
import type { StreamingProvider } from 'src/lib/movieinfo/movieinfotypes';
import type { IStreamingGenre } from "src/lib/streaming/StreamingReleasesFilter";
import { INTIAL_STATE, filterReducer, matchesFilter, STREAMING_GENRES, type ActionType } from "src/lib/streaming/StreamingReleasesFilter";
import type { Dispatch } from 'react';
import React, { useEffect, useReducer } from 'react';
import type { AdUnit } from "src/lib/types/ads/AdUnit";
import { useRouter, useSearchParams } from "next/navigation";

//#region [Props]
type ReleaseListProps = {
  weeklyReleases: IWeeklyReleases[];
  adUnits: AdUnit[];
  streamingProviders: StreamingProvider[];
} & SupportsScrollRestoration;
//#endregion

//#region [Component]
function ReleaseList({
  weeklyReleases,
  adUnits,
  streamingProviders,
  isReadyForScrollRestoration
}: ReleaseListProps) {
  const [filterState, filterDispatch] = useReducer(filterReducer, INTIAL_STATE);
  const filteredReleases = weeklyReleases.map(releaseWeek => ({
    ...releaseWeek,
    releases: releaseWeek.releases.filter(release => matchesFilter(release, filterState))
  }));
  const router = useRouter();
  const searchParams = useSearchParams();
  useEffect(() => {
    // init state based on url params
    parseInitialUrlState(searchParams, streamingProviders, filterDispatch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    isReadyForScrollRestoration?.(true);
  }, [filterState.selectedGenres, filterState.selectedProviders, weeklyReleases, isReadyForScrollRestoration]);
  useEffect(() => {
    // Update the url on a filter change
    const newUrl: string[] = [];
    if (filterState.selectedGenres.length > 0) {
      newUrl.push(`genres=${filterState.selectedGenres.map(g => g.shortName ?? g.name.substring(0, 3)).join("").toLowerCase()}`);
    }
    if (filterState.selectedProviders.length > 0) {
      newUrl.push(`providers=${filterState.selectedProviders.map(p => p.name!.substring(0, 4)).join("").toLowerCase().replace(URL_REPLACE_REGEXP, "_")}`);
    }
    if (newUrl.length > 0) {
      console.debug("Updating url status", newUrl);
      router.replace(`?${newUrl.join("&")}`, {
        scroll: false
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState]);
  const isFiltered = filterState.selectedGenres.length > 0 || filterState.selectedProviders.length > 0;
  const filterButtonText = isFiltered ? `Filter Valgt` : undefined;
  return <>
        {filteredReleases.map((wr, idx) => <React.Fragment key={wr.weekLabel}>
            <StreamingReleasesForWeek releases={wr} showFilteringOptions={() => filterDispatch({
        type: 'show'
      })} filterButtonText={filterButtonText} />

            {idx < 5 && <Ad targetId={adUnits[idx].targetId!} height="300" />}
        </React.Fragment>)}
        {filterState.showFilter && <Filter streamingProviders={streamingProviders} filterDispatch={filterDispatch} filterState={filterState} />}
    </>;
}
export default withScrollRestoration(ReleaseList);
//#endregion

//#region [Styles]

//#endregion

//#region [Other] Helpers
const URL_REPLACE_REGEXP = /[-._~:/?#[\]@!$&'()*+,;= ]/g;
function parseInitialUrlState(searchParams: ReturnType<typeof useSearchParams>, providers: StreamingProvider[], filterDispatch: Dispatch<ActionType>) {
  // read state from url
  if (searchParams && searchParams.size > 0) {
    let doInitFilter = false;
    let genres: IStreamingGenre[] = [];
    let _providers: StreamingProvider[] = [];
    if (searchParams.has("genres")) {
      const urlGenres = (searchParams.get("genres")!.toLowerCase().match(/.{1,3}/g) ?? []) as string[];
      genres = STREAMING_GENRES.filter(sg => urlGenres.includes((sg.shortName ?? sg.name.substring(0, 3)).toLowerCase()));
      doInitFilter = doInitFilter || genres.length > 0;
      console.debug(urlGenres, genres, doInitFilter);
    }
    if (searchParams.has("providers")) {
      const urlProviders = (searchParams.get("providers")!.toLowerCase().match(/.{1,4}/g) ?? []) as string[];
      _providers = providers.filter(p => urlProviders.includes(p.name!.substring(0, 4).toLowerCase().replace(URL_REPLACE_REGEXP, "_")));
      doInitFilter = doInitFilter || _providers.length > 0;
    }
    if (doInitFilter) {
      console.debug("Updating filter state", genres, _providers);
      filterDispatch({
        type: "preinitfilter",
        genres,
        providers: _providers
      });
    }
    console.groupEnd();
  }
}
//#endregion